import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import Combobox from "../components/Combobox";

const ATC_OPTIONS = ["1-1", "2-1", "3", "4-1", "6-1", "7-1", "8-1", "9"];
const EQUIPMENT_OPTIONS = [
  "ATC", "ATC-1-1", "ATC-2-1", "ATC-3", "ATC-6-1", "ATC-7-1", "ATC-8-1", "ATC-9", "PR-401", "PR-402", "PR-403", "PR-404", "SH-401", "RSM", "IS-402",
  "GC-401", "DR-401", "IF-401 A", "IF-401 B", "IF-401 C", "CS-401", "SP-401", "HF-401", "AC-401",
];

const todayIso = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const emptyCasting = () => ({ no_lot: "", total: "" });
const emptyProblem = () => ({ waktu_mulai: "", waktu_selesai: "", equipment: "", problem: "", countermeasure: "" });
const emptyInfo = () => ({ judul_informasi: "", isian_informasi: "" });
const emptyMaterialMasuk = () => ({ name: "", kg: "" });
const defaultInfos = () => Array.from({ length: 15 }, () => emptyInfo());
const parseMaterialMasuk = (raw) => {
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw || "[]") : raw;
    if (!Array.isArray(parsed)) return [emptyMaterialMasuk()];
    const rows = parsed.map((it) => ({
      name: String(it?.name || ""),
      kg: String(it?.kg ?? ""),
    }));
    return rows.length ? rows : [emptyMaterialMasuk()];
  } catch {
    return [emptyMaterialMasuk()];
  }
};

const initialState = {
  tanggal: todayIso(),
  shift: "",
  schedule: "",
  team_operasi: "",
  team_if: "A",
  nama_staff: "",
  castings: [emptyCasting(), emptyCasting(), emptyCasting(), emptyCasting()],
  pengirimans: [emptyCasting(), emptyCasting(), emptyCasting(), emptyCasting()],
  kadar_gc401: "",
  ce: "",
  sisa_butt: "",
  ac401: "",
  hf401: "",
  masuk_metal_sca: "",
  rand_crust: "",
  ladle_dross: "",
  pengiriman_crust_over_30: "",
  pengiriman_aluminium_ball: "",
  pengiriman_metal_scrap: "",
  pengiriman_dust_hammer_mill: "",
  pengiriman_aluminium_scrap: "",
  pengiriman_material_masuk: [emptyMaterialMasuk()],
  atc_aktif: [],
  available_atc: "",
  forklift_aktif: "",
  sweeper_aktif: "",
  ht: "",
  butt_diterima: "",
  berat_rata_rata: "",
  tinggi_rata_rata: "",
  butt_tebal: "",
  butt_siluman: "",
  sticking_kecil: "",
  sticking_besar: "",
  sticking_gosong: "",
  impeller_1: "",
  impeller_2: "",
  impeller_3: "",
  lng_2330: "",
  problems: [emptyProblem(), emptyProblem()],
  infos: defaultInfos(),
};

export default function InputForm() {
  const { id } = useParams(); // edit mode when id provided
  const nav = useNavigate();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const isEdit = Boolean(id);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/reports/${id}`);
        const m = data.main || {};
        setForm({
          tanggal: m.tanggal || todayIso(),
          shift: m.shift || "1",
          schedule: m.schedule || "",
          team_operasi: m.team_operasi || "",
          team_if: m.team_if || "A",
          nama_staff: m.nama_staff || "",
          castings: (data.castings || []).length ? data.castings.map(c => ({ no_lot: c.no_lot, total: c.total })) : [emptyCasting()],
          pengirimans: (data.pengirimans || []).length ? data.pengirimans.map(c => ({ no_lot: c.no_lot, total: c.total })) : [emptyCasting()],
          kadar_gc401: m.kadar_gc401 || "",
          ce: m.ce || "",
          sisa_butt: m.sisa_butt || "",
          ac401: m.ac401 || "",
          hf401: m.hf401 || "",
          masuk_metal_sca: m.masuk_metal_sca || "",
          rand_crust: m.rand_crust || "",
          ladle_dross: m.ladle_dross || "",
          pengiriman_crust_over_30: m.pengiriman_crust_over_30 || "",
          pengiriman_aluminium_ball: m.pengiriman_aluminium_ball || "",
          pengiriman_metal_scrap: m.pengiriman_metal_scrap || "",
          pengiriman_dust_hammer_mill: m.pengiriman_dust_hammer_mill || "",
          pengiriman_aluminium_scrap: m.pengiriman_aluminium_scrap || "",
          pengiriman_material_masuk: parseMaterialMasuk(m.pengiriman_material_masuk_json),
          atc_aktif: (m.atc_aktif ? String(m.atc_aktif).split(",").filter(Boolean) : []),
          available_atc: m.available_atc || "",
          forklift_aktif: m.forklift_aktif || "",
          sweeper_aktif: m.sweeper_aktif || "",
          ht: m.ht || "",
          butt_diterima: m.butt_diterima || "",
          berat_rata_rata: m.berat_rata_rata || "",
          tinggi_rata_rata: m.tinggi_rata_rata || "",
          butt_tebal: m.butt_tebal || "",
          butt_siluman: m.butt_siluman || "",
          sticking_kecil: m.sticking_kecil || "",
          sticking_besar: m.sticking_besar || "",
          sticking_gosong: m.sticking_gosong || "",
          impeller_1: m.impeller_1 || "",
          impeller_2: m.impeller_2 || "",
          impeller_3: m.impeller_3 || "",
          lng_2330: m.lng_2330 || "",
          problems: (data.problems || []).length ? data.problems.map(p => ({
            waktu_mulai: p.waktu_mulai || "", waktu_selesai: p.waktu_selesai || "", equipment: p.equipment, problem: p.problem, countermeasure: p.countermeasure,
          })) : [emptyProblem()],
          infos: (data.infos || []).length ? data.infos.map(i => ({
            judul_informasi: i.judul_informasi, isian_informasi: i.isian_informasi,
          })) : [emptyInfo()],
        });
      } catch (e) {
        toast.error("Gagal memuat data laporan");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const totalCasting = useMemo(
    () => form.castings.reduce((s, r) => s + (parseFloat(r.total) || 0), 0),
    [form.castings],
  );
  const totalPengiriman = useMemo(
    () => form.pengirimans.reduce((s, r) => s + (parseFloat(r.total) || 0), 0),
    [form.pengirimans],
  );
  const scheduleNum = parseFloat(form.schedule) || 0;
  const selisihCasting = totalCasting - scheduleNum;
  const selisihPengiriman = totalPengiriman - scheduleNum;

  const updateRow = (key, idx, field, value) => {
    setForm((s) => {
      const arr = [...s[key]];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...s, [key]: arr };
    });
  };
  const addRow = (key, empty) => setForm((s) => ({ ...s, [key]: [...s[key], empty()] }));
  const removeRow = (key, idx) =>
    setForm((s) => ({ ...s, [key]: s[key].filter((_, i) => i !== idx) }));

  const toggleAtc = (val) => {
    setForm((s) => ({
      ...s,
      atc_aktif: s.atc_aktif.includes(val)
        ? s.atc_aktif.filter((x) => x !== val)
        : [...s.atc_aktif, val],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.shift) {
      toast.error("Shift wajib dipilih");
      return;
    }
    if (!form.nama_staff.trim()) {
      toast.error("Nama Staff wajib diisi");
      return;
    }
    setSubmitting(true);
    try {
      const isShift2 = String(form.shift) === "2";
      const payload = {
        ...form,
        schedule: parseFloat(form.schedule) || 0,
        castings: form.castings
          .filter((r) => r.no_lot || r.total)
          .map((r) => ({ no_lot: String(r.no_lot || ""), total: parseFloat(r.total) || 0 })),
        pengirimans: form.pengirimans
          .filter((r) => r.no_lot || r.total)
          .map((r) => ({ no_lot: String(r.no_lot || ""), total: parseFloat(r.total) || 0 })),
        problems: form.problems
          .filter((r) => r.waktu || r.equipment || r.problem || r.countermeasure),
        infos: form.infos
          .filter((r) => r.judul_informasi || r.isian_informasi),
        kadar_gc401: parseFloat(form.kadar_gc401) || 0,
        ce: parseFloat(form.ce) || 0,
        sisa_butt: parseFloat(form.sisa_butt) || 0,
        ac401: parseFloat(form.ac401) || 0,
        hf401: parseFloat(form.hf401) || 0,
        masuk_metal_sca: parseFloat(form.masuk_metal_sca) || 0,
        rand_crust: parseFloat(form.rand_crust) || 0,
        ladle_dross: parseFloat(form.ladle_dross) || 0,
        pengiriman_crust_over_30: isShift2 ? (parseFloat(form.pengiriman_crust_over_30) || 0) : 0,
        pengiriman_aluminium_ball: isShift2 ? (parseFloat(form.pengiriman_aluminium_ball) || 0) : 0,
        pengiriman_metal_scrap: isShift2 ? (parseFloat(form.pengiriman_metal_scrap) || 0) : 0,
        pengiriman_dust_hammer_mill: isShift2 ? (parseFloat(form.pengiriman_dust_hammer_mill) || 0) : 0,
        pengiriman_aluminium_scrap: isShift2 ? (parseFloat(form.pengiriman_aluminium_scrap) || 0) : 0,
        pengiriman_material_masuk: isShift2
          ? form.pengiriman_material_masuk
            .filter((r) => String(r.name || "").trim() || String(r.kg || "").trim())
            .map((r) => ({ name: String(r.name || "").trim(), kg: parseFloat(r.kg) || 0 }))
          : [],
        forklift_aktif: parseFloat(form.forklift_aktif) || 0,
        available_atc: parseFloat(form.available_atc) || 0,
        sweeper_aktif: parseFloat(form.sweeper_aktif) || 0,
        ht: parseFloat(form.ht) || 0,
        butt_diterima: parseFloat(form.butt_diterima) || 0,
        berat_rata_rata: parseFloat(form.berat_rata_rata) || 0,
        tinggi_rata_rata: parseFloat(form.tinggi_rata_rata) || 0,
        butt_tebal: parseFloat(form.butt_tebal) || 0,
        butt_siluman: parseFloat(form.butt_siluman) || 0,
        sticking_kecil: parseFloat(form.sticking_kecil) || 0,
        sticking_besar: parseFloat(form.sticking_besar) || 0,
        sticking_gosong: parseFloat(form.sticking_gosong) || 0,
        impeller_1: parseFloat(form.impeller_1) || 0,
        impeller_2: parseFloat(form.impeller_2) || 0,
        impeller_3: parseFloat(form.impeller_3) || 0,
        lng_2330: parseFloat(form.lng_2330) || 0,
      };
      if (isEdit) {
        await api.put(`/reports/${id}`, payload);
        toast.success("Laporan berhasil diupdate");
      } else {
        await api.post("/reports", payload);
        toast.success("Laporan berhasil disimpan");
      }
      nav("/data");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan laporan");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-slate-500">Memuat...</div>;
  }

  return (
    <form onSubmit={submit} className="p-4 sm:p-6 max-w-[1400px] mx-auto space-y-5" data-testid="input-form">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <div className="label-ind">Input Data</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            {isEdit ? "Edit Laporan" : "Laporan Shift Baru"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Isi semua section. Data akan tersimpan ke Google Sheets.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => nav(-1)} data-testid="btn-cancel" className="rounded-sm">
            <X className="w-4 h-4 mr-1" /> Batal
          </Button>
          <Button type="submit" disabled={submitting} data-testid="btn-submit" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
            <Save className="w-4 h-4 mr-1" /> {submitting ? "Menyimpan..." : (isEdit ? "Update" : "Submit")}
          </Button>
        </div>
      </div>

      {/* Informasi Shift */}
      <Section title="Informasi Shift">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <FieldWrap label="Tanggal">
            <Input type="date" value={form.tanggal} onChange={(e) => setField("tanggal", e.target.value)} data-testid="input-tanggal" className="h-9 rounded-sm" required />
          </FieldWrap>
          <FieldWrap label="Shift">
            <Select value={form.shift} onValueChange={(v) => setField("shift", v)}>
              <SelectTrigger className="h-9 rounded-sm" data-testid="select-shift"><SelectValue placeholder="Pilih shift" /></SelectTrigger>
              <SelectContent>
                {["1", "2", "3"].map((v) => <SelectItem key={v} value={v}>Shift {v}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldWrap>
          <FieldWrap label="Schedule">
            <Input type="number" step="any" value={form.schedule} onChange={(e) => setField("schedule", e.target.value)} data-testid="input-schedule" className="h-9 rounded-sm font-mono" placeholder="0" />
          </FieldWrap>
          <FieldWrap label="Team Operasi">
            <Select value={form.team_operasi} onValueChange={(v) => setField("team_operasi", v)}>
              <SelectTrigger className="h-9 rounded-sm" data-testid="select-team-operasi"><SelectValue placeholder="Pilih team" /></SelectTrigger>
              <SelectContent>
                {["E", "F", "G"].map((v) => <SelectItem key={v} value={v}>Team {v}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldWrap>
          <FieldWrap label="Team IF">
            <Select value={form.team_if} onValueChange={(v) => setField("team_if", v)}>
              <SelectTrigger className="h-9 rounded-sm" data-testid="select-team-if"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["A", "B", "C", "D"].map((v) => <SelectItem key={v} value={v}>IF {v}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldWrap>
          <FieldWrap label="Nama Staff">
            <Input value={form.nama_staff} onChange={(e) => setField("nama_staff", e.target.value)} data-testid="input-nama-staff" className="h-9 rounded-sm" placeholder="Nama staff shift" required />
          </FieldWrap>
        </div>
        {scheduleNum > 0 && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
              <div className="label-ind mb-1">Selisih Casting</div>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-xs text-slate-500">{totalCasting} − {scheduleNum} =</span>
                <span className={`font-mono text-lg font-bold ${selisihCasting >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="selisih-casting">
                  {selisihCasting >= 0 ? "+" : ""}{selisihCasting.toLocaleString("id-ID")}
                </span>
              </div>
            </div>
            <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
              <div className="label-ind mb-1">Selisih Pengiriman</div>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-xs text-slate-500">{totalPengiriman} − {scheduleNum} =</span>
                <span className={`font-mono text-lg font-bold ${selisihPengiriman >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="selisih-pengiriman">
                  {selisihPengiriman >= 0 ? "+" : ""}{selisihPengiriman.toLocaleString("id-ID")}
                </span>
              </div>
            </div>
          </div>
        )}
      </Section>

      {/* Produksi Casting */}
      <Section title="Produksi — Casting">
        <DynamicTable
          rows={form.castings}
          cols={[
            { key: "no_lot", label: "No. Lot", type: "text", testid: "casting-lot" },
            { key: "total", label: "Total", type: "number", testid: "casting-total" },
          ]}
          onChange={(i, f, v) => updateRow("castings", i, f, v)}
          onAdd={() => addRow("castings", emptyCasting)}
          onRemove={(i) => removeRow("castings", i)}
          total={totalCasting}
          testid="table-casting"
        />
      </Section>

      {/* Pengiriman AA */}
      <Section title="Produksi — Pengiriman AA">
        <DynamicTable
          rows={form.pengirimans}
          cols={[
            { key: "no_lot", label: "No. Lot", type: "text", testid: "pengiriman-lot" },
            { key: "total", label: "Total", type: "number", testid: "pengiriman-total" },
          ]}
          onChange={(i, f, v) => updateRow("pengirimans", i, f, v)}
          onAdd={() => addRow("pengirimans", emptyCasting)}
          onRemove={(i) => removeRow("pengirimans", i)}
          total={totalPengiriman}
          testid="table-pengiriman"
        />
      </Section>

      {/* Stock Metal & Material */}
      <Section title="Stock Metal & Material">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <NumField label="Kadar GC 401 (%)" value={form.kadar_gc401} onChange={(v) => setField("kadar_gc401", v)} testid="input-kadar-gc401" />
          <NumField label="CE (%)" value={form.ce} onChange={(v) => setField("ce", v)} testid="input-ce" />
          <NumField label="Sisa Butt (Pcs)" value={form.sisa_butt} onChange={(v) => setField("sisa_butt", v)} testid="input-sisa-butt" />
          <NumField label="AC-401 (%)" value={form.ac401} onChange={(v) => setField("ac401", v)} testid="input-ac401" />
          <NumField label="HF-401 (kg)" value={form.hf401} onChange={(v) => setField("hf401", v)} testid="input-hf401" />
          <NumField label="Masuk Metal SCA (Kg)" value={form.masuk_metal_sca} onChange={(v) => setField("masuk_metal_sca", v)} testid="input-masuk-metal-sca" />
          <NumField label="Rand Crust (Kg)" value={form.rand_crust} onChange={(v) => setField("rand_crust", v)} testid="input-rand-crust" />
          <NumField label="Ladle Dross (Kg)" value={form.ladle_dross} onChange={(v) => setField("ladle_dross", v)} testid="input-ladle-dross" />
        </div>
      </Section>

      {/* Pengiriman Material (Shift 2 only) */}
      {String(form.shift) === "2" && (
        <Section title="Pengiriman Material">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <NumField label="Crust Over 30 mm (kg)" value={form.pengiriman_crust_over_30} onChange={(v) => setField("pengiriman_crust_over_30", v)} testid="input-pm-crust-over-30" />
            <NumField label="Aluminium ball (kg)" value={form.pengiriman_aluminium_ball} onChange={(v) => setField("pengiriman_aluminium_ball", v)} testid="input-pm-aluminium-ball" />
            <NumField label="Metal Scrap (kg)" value={form.pengiriman_metal_scrap} onChange={(v) => setField("pengiriman_metal_scrap", v)} testid="input-pm-metal-scrap" />
            <NumField label="Dust Hammer mill (kg)" value={form.pengiriman_dust_hammer_mill} onChange={(v) => setField("pengiriman_dust_hammer_mill", v)} testid="input-pm-dust-hammer-mill" />
            <NumField label="Aluminium Scrap (kg)" value={form.pengiriman_aluminium_scrap} onChange={(v) => setField("pengiriman_aluminium_scrap", v)} testid="input-pm-aluminium-scrap" />
          </div>

          <div className="mt-4">
            <div className="label-ind mb-2">Material Masuk (Custom)</div>
            <div className="overflow-x-auto">
              <table className="w-full table-dense border border-slate-200">
                <thead>
                  <tr>
                    <th className="w-12 text-left">#</th>
                    <th className="text-left">Nama Material</th>
                    <th className="w-48 text-left">Kg</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.pengiriman_material_masuk.map((row, i) => (
                    <tr key={i}>
                      <td className="text-slate-400 font-mono">{i + 1}</td>
                      <td>
                        <Input
                          value={row.name}
                          onChange={(e) => updateRow("pengiriman_material_masuk", i, "name", e.target.value)}
                          data-testid={`pm-custom-name-${i}`}
                          className="h-9 rounded-sm"
                          placeholder="Nama material"
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          step="any"
                          value={row.kg}
                          onChange={(e) => updateRow("pengiriman_material_masuk", i, "kg", e.target.value)}
                          data-testid={`pm-custom-kg-${i}`}
                          className="h-9 rounded-sm font-mono"
                          placeholder="0"
                        />
                      </td>
                      <td>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                          onClick={() => removeRow("pengiriman_material_masuk", i)}
                          disabled={form.pengiriman_material_masuk.length === 1}
                          data-testid={`pm-custom-remove-${i}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addRow("pengiriman_material_masuk", emptyMaterialMasuk)}
              data-testid="btn-add-pm-custom"
              className="mt-2 rounded-sm"
            >
              <Plus className="w-4 h-4 mr-1" /> Tambah Baris
            </Button>
          </div>
        </Section>
      )}

      {/* Peralatan */}
      <Section title="Peralatan">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="md:col-span-2">
            <div className="label-ind mb-2">ATC Aktif</div>
            <div className="grid grid-cols-4 gap-2">
              {ATC_OPTIONS.map((opt) => {
                const active = form.atc_aktif.includes(opt);
                return (
                  <label
                    key={opt}
                    data-testid={`checkbox-atc-${opt}`}
                    className={`flex items-center gap-2 px-3 py-2 border rounded-sm cursor-pointer text-sm select-none ${
                      active ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <Checkbox
                      checked={active}
                      onCheckedChange={() => toggleAtc(opt)}
                      className="rounded-sm"
                    />
                    <span className="font-mono text-sm font-semibold">{opt}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <NumField label="Available ATC" value={form.available_atc} onChange={(v) => setField("available_atc", v)} testid="input-available-atc" />
          <NumField label="Forklift Aktif" value={form.forklift_aktif} onChange={(v) => setField("forklift_aktif", v)} testid="input-forklift" />
          <NumField label="Sweeper Aktif" value={form.sweeper_aktif} onChange={(v) => setField("sweeper_aktif", v)} testid="input-sweeper" />
          <NumField label="HT" value={form.ht} onChange={(v) => setField("ht", v)} testid="input-ht" />
        </div>
      </Section>

      {/* Kondisi Butt */}
      <Section title="Kondisi Butt">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <NumField label="Butt Diterima" value={form.butt_diterima} onChange={(v) => setField("butt_diterima", v)} testid="input-butt-diterima" />
          <NumField label="Berat Rata Rata" value={form.berat_rata_rata} onChange={(v) => setField("berat_rata_rata", v)} testid="input-berat-rata-rata" />
          <NumField label="Tinggi Rata Rata" value={form.tinggi_rata_rata} onChange={(v) => setField("tinggi_rata_rata", v)} testid="input-tinggi-rata-rata" />
          <NumField label="Total Butt Tebal" value={form.butt_tebal} onChange={(v) => setField("butt_tebal", v)} testid="input-butt-tebal" />
          <NumField label="Total Butt Siluman" value={form.butt_siluman} onChange={(v) => setField("butt_siluman", v)} testid="input-butt-siluman" />
          <NumField label="Reject Sticking Kecil" value={form.sticking_kecil} onChange={(v) => setField("sticking_kecil", v)} testid="input-sticking-kecil" />
          <NumField label="Reject Sticking Besar" value={form.sticking_besar} onChange={(v) => setField("sticking_besar", v)} testid="input-sticking-besar" />
          <NumField label="Reject Sticking Gosong" value={form.sticking_gosong} onChange={(v) => setField("sticking_gosong", v)} testid="input-sticking-gosong" />
          <NumField label="Impeller 1" value={form.impeller_1} onChange={(v) => setField("impeller_1", v)} testid="input-impeller-1" />
          <NumField label="Impeller 2" value={form.impeller_2} onChange={(v) => setField("impeller_2", v)} testid="input-impeller-2" />
          <NumField label="Impeller 3" value={form.impeller_3} onChange={(v) => setField("impeller_3", v)} testid="input-impeller-3" />
        </div>
      </Section>
      {/* Data LNG */}
      <Section title="Data LNG">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <NumField label="Data LNG 23:30 (Nm³)" value={form.lng_2330} onChange={(v) => setField("lng_2330", v)} testid="input-lng-2330" />
        </div>
      </Section>

      {/* Problem & Countermeasure */}
      <Section title="Problem & Countermeasure">
        <div className="overflow-x-auto">
          <table className="w-full table-dense border border-slate-200">
            <thead>
              <tr>
                <th className="w-24">Waktu Mulai</th>
                <th className="w-24">Waktu Selesai</th>
                <th className="w-44">Equipment</th>
                <th>Problem</th>
                <th>Countermeasure</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {form.problems.map((row, i) => (
                <tr key={i}>
                  <td>
                    <Input type="time" value={row.waktu_mulai} onChange={(e) => updateRow("problems", i, "waktu_mulai", e.target.value)} data-testid={`problem-waktu-mulai-${i}`} className="h-9 rounded-sm" />
                  </td>
                  <td>
                    <Input type="time" value={row.waktu_selesai} onChange={(e) => updateRow("problems", i, "waktu_selesai", e.target.value)} data-testid={`problem-waktu-selesai-${i}`} className="h-9 rounded-sm" />
                  </td>
                  <td>
                    <Combobox value={row.equipment} onChange={(v) => updateRow("problems", i, "equipment", v)} options={EQUIPMENT_OPTIONS} placeholder="Pilih alat" testid={`problem-equipment-${i}`} />
                  </td>
                  <td>
                    <Input value={row.problem} onChange={(e) => updateRow("problems", i, "problem", e.target.value)} data-testid={`problem-desc-${i}`} className="h-9 rounded-sm" placeholder="Deskripsi masalah" />
                  </td>
                  <td>
                    <Input value={row.countermeasure} onChange={(e) => updateRow("problems", i, "countermeasure", e.target.value)} data-testid={`problem-cm-${i}`} className="h-9 rounded-sm" placeholder="Tindakan" />
                  </td>
                  <td>
                    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => removeRow("problems", i)} data-testid={`problem-remove-${i}`} disabled={form.problems.length === 1}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => addRow("problems", emptyProblem)} data-testid="btn-add-problem" className="mt-2 rounded-sm">
          <Plus className="w-4 h-4 mr-1" /> Tambah Baris
        </Button>
      </Section>

      {/* Informasi Lainnya */}
      <Section title="Informasi Lainnya">
        <div className="overflow-x-auto">
          <table className="w-full table-dense border border-slate-200">
            <thead>
              <tr>
                <th className="w-12 text-left">#</th>
                <th className="w-[35%]">Judul Informasi</th>
                <th>Isian Informasi</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {form.infos.map((row, i) => (
                <tr key={i}>
                  <td className="text-slate-400 font-mono">{i + 1}</td>
                  <td>
                    <Input value={row.judul_informasi} onChange={(e) => updateRow("infos", i, "judul_informasi", e.target.value)} data-testid={`info-judul-${i}`} className="h-9 rounded-sm" placeholder="cth: Catatan Safety" />
                  </td>
                  <td>
                    <Input value={row.isian_informasi} onChange={(e) => updateRow("infos", i, "isian_informasi", e.target.value)} data-testid={`info-isian-${i}`} className="h-9 rounded-sm" placeholder="cth: Pastikan APD lengkap" />
                  </td>
                  <td>
                    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => removeRow("infos", i)} data-testid={`info-remove-${i}`} disabled={form.infos.length === 1}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => addRow("infos", emptyInfo)} data-testid="btn-add-info" className="mt-2 rounded-sm">
          <Plus className="w-4 h-4 mr-1" /> Tambah Baris
        </Button>
      </Section>

      <div className="flex justify-end gap-2 pt-2 pb-8">
        <Button type="button" variant="outline" onClick={() => nav(-1)} className="rounded-sm">Batal</Button>
        <Button type="submit" disabled={submitting} data-testid="btn-submit-bottom" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white min-w-[160px]">
          <Save className="w-4 h-4 mr-1" /> {submitting ? "Menyimpan..." : (isEdit ? "Update Laporan" : "Submit Laporan")}
        </Button>
      </div>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <Card className="rounded-sm bg-white border-slate-200 p-0 overflow-hidden">
      <div className="bg-slate-900 text-white px-4 py-2">
        <div className="text-xs font-bold uppercase tracking-[0.12em]">{title}</div>
      </div>
      <div className="p-4">{children}</div>
    </Card>
  );
}

function FieldWrap({ label, children }) {
  return (
    <div>
      <Label className="label-ind">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <FieldWrap label={label}>
      <Input
        type="number"
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="h-9 rounded-sm font-mono"
        placeholder="0"
      />
    </FieldWrap>
  );
}

function DynamicTable({ rows, cols, onChange, onAdd, onRemove, total, testid }) {
  return (
    <div data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full table-dense border border-slate-200">
          <thead>
            <tr>
              <th className="w-12 text-left">#</th>
              {cols.map((c) => <th key={c.key} className="text-left">{c.label}</th>)}
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td className="text-slate-400 font-mono">{i + 1}</td>
                {cols.map((c) => (
                  <td key={c.key}>
                    <Input
                      type={c.type}
                      value={row[c.key] ?? ""}
                      onChange={(e) => onChange(i, c.key, e.target.value)}
                      data-testid={`${c.testid}-${i}`}
                      className="h-9 rounded-sm"
                      step="any"
                    />
                  </td>
                ))}
                <td>
                  <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => onRemove(i)} disabled={rows.length === 1}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td></td>
              <td>Total</td>
              <td className="font-mono text-base" data-testid={`${testid}-total`}>{Number(total || 0).toLocaleString("id-ID")}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onAdd} className="mt-2 rounded-sm" data-testid={`${testid}-add`}>
        <Plus className="w-4 h-4 mr-1" /> Tambah Baris
      </Button>
    </div>
  );
}

